import { create } from "zustand";
import { useMemo } from "react";
import type { InstanceProfile, InstanceSetting } from "@/api/types";
import { instanceService } from "@/api/services";

/**
 * Instance bootstrap state: profile (version, branding, needsSetup) plus the
 * public settings needed to render before/without authentication.
 *
 * The backend redacts sensitive fields for anonymous callers
 * (`public-with-field-redaction`), so this is safe to load pre-login.
 */

export interface BrandInfo {
  title: string;
  description: string;
  logoUrl?: string;
}

const DEFAULT_BRAND: BrandInfo = {
  title: "Memos",
  description: "A privacy-first, lightweight note-taking service.",
};

export type InstanceStatus = "idle" | "loading" | "ready" | "error";

interface InstanceState {
  status: InstanceStatus;
  profile: InstanceProfile | null;
  settings: InstanceSetting[];
  error: string | null;

  load: () => Promise<void>;
  reload: () => Promise<void>;
  setSettings: (settings: InstanceSetting[]) => void;
}

export const useInstanceStore = create<InstanceState>((set, get) => ({
  status: "idle",
  profile: null,
  settings: [],
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const profile = await instanceService.getProfile();
      set({ profile });
      // needsSetup instances have no public settings to fetch yet.
      if (!profile.needsSetup) {
        try {
          const res = await instanceService.batchGetSettings(["general", "memo-related", "storage"]);
          set({ settings: res.settings ?? [] });
        } catch {
          // Public settings are best-effort; anonymous/private instances may 403.
          set({ settings: [] });
        }
      }
      set({ status: "ready" });
    } catch (e) {
      set({ status: "error", error: e instanceof Error ? e.message : "Failed to load instance" });
    }
  },

  reload: async () => {
    set({ status: "idle" });
    await get().load();
  },

  setSettings: (settings) => set({ settings }),
}));

// Selectors ------------------------------------------------------------------

function findSetting(settings: InstanceSetting[], key: string): InstanceSetting | undefined {
  return settings.find((s) => s.name === key || s.name?.endsWith(`/${key}`) || settingKind(s) === key);
}

function settingKind(s: InstanceSetting): string | undefined {
  if (s.generalSetting) return "general";
  if (s.storageSetting) return "storage";
  if (s.memoRelatedSetting) return "memo-related";
  if (s.notificationSetting) return "notification";
  return undefined;
}

export function useGeneralSetting() {
  return useInstanceStore((s) => findSetting(s.settings, "general")?.generalSetting);
}
export function useMemoRelatedSetting() {
  return useInstanceStore((s) => findSetting(s.settings, "memo-related")?.memoRelatedSetting);
}
export function useStorageSetting() {
  return useInstanceStore((s) => findSetting(s.settings, "storage")?.storageSetting);
}
export function useInstanceProfile() {
  return useInstanceStore((s) => s.profile);
}

export function useBrand(): BrandInfo {
  const custom = useInstanceStore((s) => findSetting(s.settings, "general")?.generalSetting?.customProfile);

  return useMemo(
    () => ({
      title: custom?.title || DEFAULT_BRAND.title,
      description: custom?.description || DEFAULT_BRAND.description,
      logoUrl: custom?.logoUrl || undefined,
    }),
    [custom],
  );
}

/** Whether anonymous visitors may browse public content (open instance). */
export function useIsOpenInstance(): boolean {
  return useInstanceStore((s) => Boolean(s.profile?.instanceUrl));
}

export function useNeedsSetup(): boolean {
  return useInstanceStore((s) => Boolean(s.profile?.needsSetup));
}

/** Apply administrator-configured custom CSS (and surface a JS warning). */
export function useCustomStyle(): string | undefined {
  return useInstanceStore((s) => findSetting(s.settings, "general")?.generalSetting?.additionalStyle || undefined);
}
export function useCustomScript(): string | undefined {
  return useInstanceStore((s) => findSetting(s.settings, "general")?.generalSetting?.additionalScript || undefined);
}
